/**
 * Web Audio API 游戏音效与语音合成引擎
 * 纯原生 Web Audio + Web SpeechSynthesis，无外部静态音频文件依赖
 * 支持：全套卡牌报牌语音、出牌打桌面竹木敲击、吃碰提跑胡音效、轮到出牌提醒、过牌音效、流局音效、金币音效
 */

class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;
  private volume: number = 0.6;
  private voiceEnabled: boolean = true;
  private bgmOscs: OscillatorNode[] = [];
  private bgmInterval: any = null;
  private bgmPlaying: boolean = false;

  private getContext(): AudioContext | null {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.ctx = new AudioContextClass();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled && this.bgmPlaying) {
      this.stopBGM();
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setVoiceEnabled(enabled: boolean): void {
    this.voiceEnabled = enabled;
  }

  public isVoiceEnabled(): boolean {
    return this.voiceEnabled;
  }

  public setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
  }

  public getVolume(): number {
    return this.volume;
  }

  /**
   * 点击/选牌音效
   */
  public playClick(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.05);

    gain.gain.setValueAtTime(this.volume * 0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.05);
  }

  /**
   * 轮到你的回合提示音 (清脆叮咚双音)
   */
  public playTurnAlert(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const notes = [587.33, 880]; // D5, A5
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.1);

      gain.gain.setValueAtTime(this.volume * 0.45, ctx.currentTime + idx * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.1 + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + idx * 0.1);
      osc.stop(ctx.currentTime + idx * 0.1 + 0.25);
    });
  }

  /**
   * 摸牌音效 (轻快滑片音)
   */
  public playDraw(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(300, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(700, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(this.volume * 0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  }

  /**
   * 打牌音效 (实木拍桌清脆质感)
   */
  public playDiscard(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    // 低频拍打声 + 高频敲击泛音
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(280, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.08);

    osc2.type = 'square';
    osc2.frequency.setValueAtTime(620, ctx.currentTime);
    osc2.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.05);

    gain.gain.setValueAtTime(this.volume * 0.6, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.09);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start();
    osc2.start();
    osc1.stop(ctx.currentTime + 0.09);
    osc2.stop(ctx.currentTime + 0.09);
  }

  /**
   * 报牌语音：播报单张牌名称 (如: "大字壹", "小字二", "拾" 等)
   */
  public speakCard(card: { type: string; value: number; isBig?: boolean; name?: string }, isDrawn: boolean = false): void {
    if (!this.enabled || !this.voiceEnabled) return;

    const bigNames: Record<number, string> = {
      1: '大字壹', 2: '大字贰', 3: '大字叁', 4: '大字肆', 5: '大字伍',
      6: '大字陆', 7: '大字柒', 8: '大字捌', 9: '大字玖', 10: '大字拾'
    };
    const smallNames: Record<number, string> = {
      1: '小字一', 2: '小字二', 3: '小字三', 4: '小字四', 5: '小字五',
      6: '小字六', 7: '小字七', 8: '小字八', 9: '小字九', 10: '小字十'
    };

    const isBig = card.isBig !== undefined ? card.isBig : card.type === 'big';
    const cardVoiceText = isBig ? bigNames[card.value] || '大字' : smallNames[card.value] || '小字';

    this.speakDialect(cardVoiceText);
  }

  /**
   * 吃牌音效 (双音上行 + 语音)
   */
  public playChi(): void {
    if (!this.enabled) return;
    this.playToneSequence([523.25, 659.25], 0.08);
    this.speakDialect('吃！');
  }

  /**
   * 碰牌音效 (清脆三和弦 + 语音)
   */
  public playPeng(): void {
    if (!this.enabled) return;
    this.playToneSequence([587.33, 739.99, 880.0], 0.09);
    this.speakDialect('碰！');
  }

  /**
   * 跑牌 / 提牌音效 (激昂四和弦 + 语音)
   */
  public playTiPao(actionName: string): void {
    if (!this.enabled) return;
    this.playToneSequence([440, 554.37, 659.25, 880], 0.1);
    this.speakDialect(`${actionName}！`);
  }

  /**
   * 过牌 / 放弃音效
   */
  public playPass(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(this.volume * 0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.08);

    this.speakDialect('过');
  }

  /**
   * 流局 / 荒庄音效
   */
  public playDrawGame(): void {
    if (!this.enabled) return;
    // 小三和弦下行
    this.playToneSequence([440, 392, 349.23, 261.63], 0.15);
    this.speakDialect('荒庄了！');
  }

  /**
   * 金币/积分奖励音效
   */
  public playCoinReward(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const freqs = [987.77, 1318.51, 1567.98, 2093.0]; // B5, E6, G6, C7
    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.06);

      gain.gain.setValueAtTime(this.volume * 0.35, ctx.currentTime + idx * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.06 + 0.15);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + idx * 0.06);
      osc.stop(ctx.currentTime + idx * 0.06 + 0.15);
    });
  }

  /**
   * 胡牌胜利大吉大礼弦 (胜利凯歌音效)
   */
  public playWin(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C E G C
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.12);

      gain.gain.setValueAtTime(0, ctx.currentTime + idx * 0.12);
      gain.gain.linearRampToValueAtTime(this.volume * 0.5, ctx.currentTime + idx * 0.12 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.12 + 0.4);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + idx * 0.12);
      osc.stop(ctx.currentTime + idx * 0.12 + 0.4);
    });

    this.speakDialect('胡啦！大捷！');
  }

  private playToneSequence(frequencies: number[], noteDuration: number): void {
    const ctx = this.getContext();
    if (!ctx) return;

    frequencies.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * noteDuration);

      gain.gain.setValueAtTime(this.volume * 0.4, ctx.currentTime + idx * noteDuration);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (idx + 1) * noteDuration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + idx * noteDuration);
      osc.stop(ctx.currentTime + (idx + 1) * noteDuration);
    });
  }

  /**
   * 中文方言/普通话语音播报
   */
  public speakDialect(text: string): void {
    if (!this.enabled || !this.voiceEnabled || typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }

    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'zh-CN';
      utterance.rate = 1.2;
      utterance.pitch = 1.05;
      utterance.volume = this.volume;
      window.speechSynthesis.speak(utterance);
    } catch {
      // 忽略部分不支持 speech 的环境
    }
  }

  /**
   * 启动茶馆背景轻音乐氛围
   */
  public toggleBGM(enable?: boolean): boolean {
    const shouldPlay = enable !== undefined ? enable : !this.bgmPlaying;
    if (shouldPlay) {
      this.startBGM();
    } else {
      this.stopBGM();
    }
    return this.bgmPlaying;
  }

  public isBGMPlaying(): boolean {
    return this.bgmPlaying;
  }

  private startBGM(): void {
    if (this.bgmPlaying || !this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    this.bgmPlaying = true;
    const pentatonicNotes = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25]; // 宫商角徵羽 C D E G A C
    let step = 0;

    this.bgmInterval = setInterval(() => {
      if (!this.bgmPlaying || !this.enabled) return;
      const ctxCur = this.getContext();
      if (!ctxCur) return;

      const note = pentatonicNotes[(step + Math.floor(Math.random() * 3)) % pentatonicNotes.length];
      const osc = ctxCur.createOscillator();
      const gain = ctxCur.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(note, ctxCur.currentTime);

      gain.gain.setValueAtTime(this.volume * 0.08, ctxCur.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctxCur.currentTime + 1.2);

      osc.connect(gain);
      gain.connect(ctxCur.destination);

      osc.start();
      osc.stop(ctxCur.currentTime + 1.2);
      step++;
    }, 1500);
  }

  private stopBGM(): void {
    this.bgmPlaying = false;
    if (this.bgmInterval) {
      clearInterval(this.bgmInterval);
      this.bgmInterval = null;
    }
  }
}

export const soundManager = new SoundManager();